import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Icon,
  IconButton,
} from '@material-ui/core';
import Helmet from 'react-helmet';
import { useSnackbar } from 'notistack';
import SectionCard from '../../components/SectionCard';
import Avatar from './Avatar';
import useStyles from './styles';
import Actions from '../../store/ducks/userProfile';

const Profile = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { status, name, email, avatar } = useSelector(
    state => state.userProfile,
  );

  const userProfile = useMemo(
    () => ({
      name,
      email,
      avatar,
    }),
    [avatar, email, name],
  );

  const [loading, setLoading] = useState({
    avatar: false,
    name: false,
    password: false,
  });

  const [values, setValues] = useState({
    name: '',
    email: '',
    password: '',
    showPassword: {
      password: false,
      newPassword: false,
      confirmNewPassword: false,
    },
    newPassword: '',
    confirmNewPassword: '',
    ...userProfile,
  });

  useEffect(() => {
    if (status.msg && status.variant) {
      setLoading({
        avatar: false,
        name: false,
        password: false,
      });
      enqueueSnackbar(status.msg, {
        variant: status.variant,
      });
      dispatch(Actions.clearStatus());
    }
    setValues(oldValues => {
      if (!oldValues.name && !oldValues.email) {
        return { ...oldValues, ...userProfile };
      }
      return oldValues;
    });
  }, [dispatch, enqueueSnackbar, status, userProfile, userProfile.email]);

  const handleChange = useCallback((event, prop) => {
    const inputValue = event.target?.value;
    setValues(oldValues => ({ ...oldValues, [prop]: inputValue }));
  }, []);

  const uploadAvatar = useCallback(
    newAvatar => {
      setLoading(oldLoading => ({ ...oldLoading, avatar: true }));
      dispatch(Actions.updateAvatar(newAvatar));
    },
    [dispatch],
  );

  const removeAvatar = useCallback(() => {
    const splittedAvatar = values.avatar.split('/');
    const fileName = splittedAvatar[splittedAvatar.length - 1];
    dispatch(Actions.removeAvatar(fileName));
  }, [dispatch, values.avatar]);

  const handleShowPassword = useCallback(
    field =>
      setValues(oldValues => ({
        ...oldValues,
        showPassword: {
          ...oldValues.showPassword,
          [field]: !oldValues.showPassword[field],
        },
      })),
    [],
  );

  const handleSavePassword = useCallback(() => {
    const { newPassword, password: oldPassword, confirmNewPassword } = values;
    if (!!oldPassword && !!newPassword && !!confirmNewPassword) {
      if (newPassword === confirmNewPassword) {
        setLoading(oldLoading => ({ ...oldLoading, password: true }));
        dispatch(Actions.updatePassword(newPassword, oldPassword));
        setValues(oldValues => ({
          ...oldValues,
          password: '',
          newPassword: '',
          confirmNewPassword: '',
        }));
      } else {
        enqueueSnackbar('A confirmação da senha deve ser igual a nova senha', {
          variant: 'error',
        });
      }
    }
  }, [dispatch, enqueueSnackbar, values]);

  return (
    <Box className={classes.container}>
      <Helmet>
        <title>Minha Conta</title>
      </Helmet>
      <Box className="content">
        <SectionCard className="account-data-container" title="Dados da conta">
          <Box className="account-data-content">
            <Box className="account-data-avatar-container">
              <Avatar
                avatar={values.avatar}
                uploadAvatar={uploadAvatar}
                removeAvatar={removeAvatar}
                loadingAvatar={loading.avatar}
              />
            </Box>
            <Box className="account-area account-data-inputs-container">
              <Box className="input-container name">
                <TextField
                  className="input"
                  id="name"
                  variant="outlined"
                  type="text"
                  label="Nome"
                  value={values.name}
                  onChange={e => handleChange(e, 'name')}
                />
              </Box>
              <Box className="input-container email">
                <TextField
                  className="input"
                  id="email"
                  variant="outlined"
                  type="email"
                  label="Email"
                  value={values.email}
                  onChange={e => handleChange(e, 'email')}
                  disabled
                />
              </Box>
            </Box>
          </Box>
          <Box className="account-footer">
            <Button
              color="primary"
              className="btn-profile"
              onClick={e => {
                e.preventDefault();
                if (values.name) {
                  setLoading({ ...loading, name: true });
                  dispatch(
                    Actions.updateProfile({
                      name: values.name,
                      avatar: values.avatar,
                    }),
                  );
                }
              }}
            >
              {loading.name ? (
                <CircularProgress
                  className="btn-login-loading"
                  color="inherit"
                  size={24}
                />
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </Box>
        </SectionCard>

        <SectionCard
          className="account-password-container"
          title="Alterar senha"
        >
          <Box className="account-password-content">
            <Box className="account-password-current-container">
              <Box className="input-container current-password">
                <TextField
                  className="input password"
                  id="password"
                  variant="outlined"
                  type={values.showPassword.password ? 'text' : 'password'}
                  label="Senha atual"
                  value={values.password}
                  onChange={e => handleChange(e, 'password')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          aria-label="toggle password visibility"
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            handleShowPassword('password');
                          }}
                        >
                          <Icon>
                            {values.showPassword.password
                              ? 'visibility_off'
                              : 'visibility'}
                          </Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
            <Box className="account-password-new-container">
              <Box className="input-container new-password">
                <TextField
                  className="input"
                  id="newPassword"
                  variant="outlined"
                  type={values.showPassword.newPassword ? 'text' : 'password'}
                  label="Nova senha"
                  value={values.newPassword}
                  onChange={e => handleChange(e, 'newPassword')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          aria-label="toggle password visibility"
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            handleShowPassword('newPassword');
                          }}
                        >
                          <Icon>
                            {values.showPassword.newPassword
                              ? 'visibility_off'
                              : 'visibility'}
                          </Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box className="input-container new-password-confirm">
                <TextField
                  className="input"
                  id="confirmNewPassword"
                  variant="outlined"
                  type={
                    values.showPassword.confirmNewPassword ? 'text' : 'password'
                  }
                  label="Confirme a nova senha"
                  value={values.confirmNewPassword}
                  onChange={e => handleChange(e, 'confirmNewPassword')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          aria-label="toggle password visibility"
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            handleShowPassword('confirmNewPassword');
                          }}
                        >
                          <Icon>
                            {values.showPassword.confirmNewPassword
                              ? 'visibility_off'
                              : 'visibility'}
                          </Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box className="account-footer">
            <Button
              color="primary"
              className="btn-profile"
              onClick={handleSavePassword}
            >
              {loading.password ? (
                <CircularProgress
                  className="btn-login-loading"
                  color="inherit"
                  size={24}
                />
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </Box>
        </SectionCard>
      </Box>
    </Box>
  );
};

export default Profile;
